import { PaymentMethod } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FieldPath, useForm } from 'react-hook-form';

import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import handleErrors from '../../api/handleErrors';
import addPaymentMethod, {
  AddPaymentDetails,
  PaymentCentralCardResponse,
} from '../../api/paymentCentral/addPaymentMethod';
import { addToWallet, AddToWalletArgs } from '../../api/wallet/addToWallet';
import { DeleteIconSmall } from '../../assets/icons/Delete';
import Button from '../../components/styledComponents/Button';
import { LinkWithNoRouter } from '../../components/styledComponents/Link';
import colors from '../../constants/colors';
import { StatusType } from '../../constants/enums';
import {
  AutoPayMessages,
  paymentTermsAndConditions,
} from '../../constants/paymentMessages';
import paymentTypeMap, {
  getPaymentCommonName,
} from '../../constants/paymentTypeNames';
import useAnalytics from '../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { usePaymentsContext } from '../../store/payments';
import { setSelectedPaymentMethod } from '../../store/paymentsReducer';
import AutoPayInfoBar from '../AutoPay/components/AutoPayInfoBar';
import EValueDialog from './EValueDialog';
import CardForm from './forms/CardForm';
import {
  cardPaymentTypeValidationMessages,
  cardPaymentTypeValidationSchema,
  CardTypeFormValues,
} from './validation';

export interface SubmitProps {
  lastFour?: string;
  paymentType?: string;
  paymentAcctFopType?: PaymentMethod;
  paymentAccountToken?: string;
}

interface CardPaymentTypeFormProps {
  cancelAddPaymentMethod: () => void;
  removeFromWallet?: () => void;
  formValues?: CardTypeFormValues;
  buttonText?: string;
  onSubmit: (res?: SubmitProps) => void;
  paymentAccountToken?: string;
  showNicknameField?: boolean;
  showSaveToWalletCheckbox?: boolean;
  savePaymentToWallet?: boolean;
  isEValue?: boolean;
  showTermsAndConditions?: boolean;
  isAutoPay?: boolean;
  isLoading?: boolean;
}

const CardPaymentTypeForm = ({
  cancelAddPaymentMethod,
  removeFromWallet,
  formValues,
  buttonText,
  onSubmit,
  paymentAccountToken,
  showNicknameField,
  showSaveToWalletCheckbox,
  savePaymentToWallet,
  isEValue = false,
  showTermsAndConditions = false,
  isAutoPay = false,
  isLoading = false,
}: CardPaymentTypeFormProps) => {
  const [cNumber, setCNumber] = useState('');
  const initialValues = formValues ?? {
    name: '',
    cardNumber: '',
    expirationDate: '',
    zipCode: '',
    nickname: '',
  };

  const {
    control,
    formState: { isValid, isSubmitting },
    setValue,
    handleSubmit,
    setError,
    trigger,
  } = useForm<CardTypeFormValues>({
    defaultValues: initialValues,
    mode: 'onTouched',
    resolver: yupResolver(cardPaymentTypeValidationSchema(!!formValues)),
  });
  const [saveToWalletSelected, setSaveToWalletSelected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { track } = useAnalytics();
  const { dispatch } = usePaymentsContext();
  const queryClient = useQueryClient();
  const { data } = useWelcomeQuery();
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const getPaymentTokenFromPaymentCentral = useMutation({
    mutationFn: (args: AddPaymentDetails) => addPaymentMethod(args),
    onError: (err: unknown) => {
      console.error(err);
      enqueueSnackbar({
        message: AutoPayMessages.FAIL,
        variant: StatusType.ERROR,
        autoHideDuration: 10000,
      });
    },
  });

  const addPaymentToWallet = useMutation({
    mutationFn: (args: AddToWalletArgs) => addToWallet(args),
    onSuccess: async () => {
      // invalidate the wallet data in the cache so it is refetched after the POST completes
      await queryClient.invalidateQueries({
        queryKey: ['wallet'],
      });
    },
  });
  const authToken = data?.authToken ?? '';
  const custKey = data?.userInfo?.custKey ?? '';

  useEffect(() => {
    if (initialValues.expirationDate) {
      trigger();
    }
  }, [initialValues.expirationDate]);

  const handleEValueSubmit = () => {
    setIsOpen(true);
  };

  const handleOnSubmit = async (values: CardTypeFormValues) => {
    closeSnackbar();

    // Submit payment method to PaymentCentral to create or update
    const response = await getPaymentTokenFromPaymentCentral.mutateAsync({
      authToken,
      ...(!formValues && { cardNumber: cNumber }),
      ...(paymentAccountToken && { paymentAccountToken }),
      expirationDate: values.expirationDate,
      name: values.name,
      zipCode: values.zipCode,
    });

    if ('paymentAccountToken' in response) {
      track('Payment Methods Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        account_type: 'Debit/Credit Card',
        status: 'Succeeded',
      });
      const successfulResponse = response as PaymentCentralCardResponse;
      const nicknameUpdated =
        formValues && values.nickname !== formValues.nickname;

      // nickname must be updated in the wallet, not payment central
      if (saveToWalletSelected || savePaymentToWallet || nicknameUpdated) {
        const accountObj = {
          paymentAccountToken: successfulResponse.paymentAccountToken,
          shortName: values.nickname ?? '',
          isPreferred: 'false',
        };

        await addPaymentToWallet.mutateAsync({
          account: accountObj,
          ownerId: custKey,
        });
      }

      const lastFour = values.cardNumber.includes(String.fromCharCode(8226))
        ? values.cardNumber.replaceAll(String.fromCharCode(8226), '')
        : values.cardNumber.slice(values.cardNumber.length - 4);

      dispatch(
        setSelectedPaymentMethod({
          analyticsAccountType: getPaymentCommonName(
            successfulResponse?.paymentCardType
          ),
          name: values.name,
          paymentToken: response.paymentAccountToken,
          paymentMethod: response.paymentAcctFopType,
          expirationDate: values.expirationDate,
          zipCode: values.zipCode,
          displayPaymentMethod: `${
            paymentTypeMap[successfulResponse?.paymentCardType]
          } ****${lastFour}`,
        })
      );

      if (isAutoPay) {
        onSubmit({
          paymentAccountToken: response.paymentAccountToken,
          paymentAcctFopType: response.paymentAcctFopType,
        });
      } else {
        onSubmit({
          paymentType: successfulResponse?.paymentCardType,
          lastFour,
        });
      }
    } else if ('error' in response) {
      const fieldWithError = handleErrors(response.error.errorMessage);
      track('Payment Methods Submitted', {
        event_type: 'User Information Entered',
        location: 'Feature Card',
        account_type: 'Debit/Credit Card',
        status: 'Failed',
      });
      if (fieldWithError) {
        setError(fieldWithError as FieldPath<CardTypeFormValues>, {
          message: cardPaymentTypeValidationMessages[fieldWithError],
        });
      } else {
        enqueueSnackbar({
          dataTestId: 'cardPaymentTypeErrorSnackBar',
          title: 'We are unable to process your request right now.',
          content: (
            <>
              Check the details and try again, try another payment method or
              call Service at{' '}
              <Link
                href="tel:+8889805650"
                underline="none"
                sx={{
                  fontSize: {
                    mobile: 16,
                    tablet: 18,
                  },
                  fontWeight: 700,
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  color: colors.csaaSpectrumBlue50,
                }}
              >
                888.980.5650
              </Link>
              .
            </>
          ),
          variant: 'error',
        });
      }
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(isEValue ? handleEValueSubmit : handleOnSubmit)}
      >
        <CardForm
          control={control}
          showNicknameField={showNicknameField}
          setCNumber={setCNumber}
          setRhfValue={setValue}
          editing={!!formValues}
        />
        {isAutoPay && (
          <Stack sx={{ pb: 3 }}>
            <AutoPayInfoBar addingCardDetails={!formValues} />
          </Stack>
        )}
        <Stack
          direction={{ mobile: 'column', tablet: 'row' }}
          justifyContent={
            showSaveToWalletCheckbox ? 'space-between' : 'flex-end'
          }
          spacing={{ mobile: 1, tablet: 0 }}
        >
          {showSaveToWalletCheckbox && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveToWalletSelected}
                  size="small"
                  sx={{
                    [`&, &.${checkboxClasses.checked}`]: {
                      color: colors.csaaBlack,
                    },
                    '&.Mui-focusVisible .MuiSvgIcon-root ': {
                      borderRadius: 1,
                      outline: `2px solid ${colors.csaaBlack}`,
                      outlineOffset: 1,
                    },
                  }}
                  onChange={() =>
                    setSaveToWalletSelected(!saveToWalletSelected)
                  }
                />
              }
              label="Save to payment methods"
              componentsProps={{
                typography: {
                  sx: {
                    fontSize: { mobile: 18 },
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                  },
                },
              }}
            />
          )}
          <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={1}>
            {removeFromWallet && (
              <Stack
                direction="row"
                component={Button}
                spacing={1}
                onClick={removeFromWallet}
                alignItems="center"
                justifyContent="center"
                sx={{
                  mr: 2,
                  cursor: 'pointer',
                  backgroundColor: 'inherit',
                  '&:focus': {
                    outline: `solid 2px ${colors.csaaSpectrumRed50}`,
                    outlineOffset: '1px',
                    border: 'none',
                    borderRadius: 1,
                    backgroundColor: 'inherit',
                  },
                  '&:hover': {
                    backgroundColor: colors.redInteractiveButtonHover,
                  },
                }}
              >
                <DeleteIconSmall />
                <Typography
                  sx={{
                    color: colors.csaaSpectrumRed50,
                    fontWeight: 700,
                    fontSize: { mobile: 18 },
                  }}
                >
                  Remove from wallet
                </Typography>
              </Stack>
            )}
            <Button
              variant="outlined"
              color="inherit"
              data-testid="paymentMethodSelectCancel"
              disableRipple
              sx={{
                width: { mobile: 1, tablet: 'auto' },
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                color: 'text.secondary',
                px: 4,
                py: 0.5,
              }}
              onClick={cancelAddPaymentMethod}
            >
              Cancel
            </Button>
            <Button
              disableRipple
              disabled={!isValid || isSubmitting}
              type="submit"
              data-testid="cardPaymentTypeFormDoneButton"
              loading={
                isSubmitting ||
                addPaymentToWallet.isPending ||
                getPaymentTokenFromPaymentCentral.isPending ||
                isLoading
              }
            >
              {buttonText}
            </Button>
          </Stack>
        </Stack>
        {showTermsAndConditions && (
          <Stack alignItems="flex-end" sx={{ pt: { mobile: 1, tablet: 3 } }}>
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: 16,
                textAlign: { mobile: 'center', tablet: 'right' },
                letterSpacing: '0.2px',
              }}
            >
              {paymentTermsAndConditions(buttonText)}
              <LinkWithNoRouter
                href={
                  isAutoPay
                    ? 'https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/auth-terms.html'
                    : 'https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/payment-terms.html'
                }
                target="_blank"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 16,
                  fontWeight: 700,
                }}
                onClick={() =>
                  track('Terms and Conditions Accessed', {
                    event_type: 'Link Accessed',
                    link: `${
                      isAutoPay ? 'AutoPay' : 'Payment'
                    } Terms and Conditions`,
                    location: 'Feature Card',
                  })
                }
              >
                {isAutoPay ? 'AutoPay' : 'Payment'} Terms and Conditions
              </LinkWithNoRouter>
              .
            </Typography>
          </Stack>
        )}
        {isEValue && (
          <EValueDialog
            open={isOpen}
            closeDialog={() => setIsOpen(false)}
            confirmClick={handleSubmit(handleOnSubmit)}
            keepClick={cancelAddPaymentMethod}
            isLoading={
              getPaymentTokenFromPaymentCentral.isPending ||
              addPaymentToWallet.isPending
            }
          />
        )}
      </form>
    </>
  );
};

export default CardPaymentTypeForm;
